import { useState, useEffect, useRef } from 'react'
import { playTick, playWinnerDing, preloadSounds } from '../lib/sounds'

// Red Carpet palette — alternating oxblood reds and champagne golds
const COLORS = ['#c8102e', '#d4af37', '#8b0a1e', '#b8941f', '#a80d26', '#e6c24a', '#6b0818', '#f1d36a']

/**
 * MovieWheel — canvas wheel with spin animation, tick sounds, winner ding.
 *
 * @param movies    Array of movie objects. Each must have a `title`; the full
 *                  object is passed back to `onWinner` unchanged.
 * @param onWinner  Called once the spin lands. Consumer is responsible for any
 *                  persistence and navigation.
 * @param subtitle  Small text under the "The Movie Wheel" label.
 */
export default function MovieWheel({ movies, onWinner, subtitle }) {
  const canvasRef = useRef(null)
  const containerRef = useRef(null)
  const animRef = useRef(null)
  const [spinning, setSpinning] = useState(false)
  const [winner, setWinner] = useState(null)
  const [rotation, setRotation] = useState(0)
  const [canvasSize, setCanvasSize] = useState(320)

  // Responsive canvas sizing — bigger in landscape/wide viewport for AirPlay.
  useEffect(() => {
    function updateSize() {
      const vw = window.innerWidth
      const vh = window.innerHeight
      const isLandscape = vw > vh
      if (isLandscape) {
        setCanvasSize(Math.min(vh * 0.75, vw * 0.55, 640))
      } else {
        setCanvasSize(Math.min(vw - 32, 360))
      }
    }
    updateSize()
    window.addEventListener('resize', updateSize)
    window.addEventListener('orientationchange', updateSize)
    return () => {
      window.removeEventListener('resize', updateSize)
      window.removeEventListener('orientationchange', updateSize)
    }
  }, [])

  useEffect(() => {
    drawWheel(rotation)
  }, [movies, rotation, canvasSize])

  useEffect(() => {
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current) }
  }, [])

  // Fire the callback once when a winner lands.
  useEffect(() => {
    if (!winner) return
    onWinner?.(winner)
  }, [winner])

  function drawWheel(rot) {
    const canvas = canvasRef.current
    if (!canvas || !movies || movies.length === 0) return

    const size = canvasSize
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext('2d')
    const center = size / 2
    const radius = center - 4
    const count = movies.length
    const arc = (2 * Math.PI) / count

    ctx.clearRect(0, 0, size, size)
    ctx.save()
    ctx.translate(center, center)
    ctx.rotate((rot * Math.PI) / 180)

    for (let i = 0; i < count; i++) {
      const startAngle = i * arc - Math.PI / 2
      const endAngle = startAngle + arc

      ctx.beginPath()
      ctx.moveTo(0, 0)
      ctx.arc(0, 0, radius, startAngle, endAngle)
      ctx.closePath()
      ctx.fillStyle = COLORS[i % COLORS.length]
      ctx.fill()
      ctx.strokeStyle = 'rgba(0,0,0,0.3)'
      ctx.lineWidth = 1
      ctx.stroke()

      ctx.save()
      ctx.rotate(startAngle + arc / 2)
      ctx.textAlign = 'right'
      ctx.fillStyle = '#fff'
      const fontSize = Math.max(12, Math.min(20, (size * 0.055) - count * 0.4))
      ctx.font = `bold ${fontSize}px -apple-system, sans-serif`
      const title = movies[i].title
      const maxLen = Math.max(12, Math.floor(22 - count * 0.4))
      const label = title.length > maxLen ? title.substring(0, maxLen - 1) + '…' : title
      ctx.fillText(label, radius - 16, fontSize / 3)
      ctx.restore()
    }

    ctx.restore()

    // Center hub
    const hubRadius = Math.max(22, size * 0.08)
    ctx.beginPath()
    ctx.arc(center, center, hubRadius, 0, 2 * Math.PI)
    ctx.fillStyle = '#000000'
    ctx.fill()
    ctx.strokeStyle = '#d4af37'
    ctx.lineWidth = 3
    ctx.stroke()
    ctx.fillStyle = '#fff'
    ctx.font = `${Math.round(hubRadius * 0.8)}px sans-serif`
    ctx.textAlign = 'center'
    ctx.fillText('🎬', center, center + hubRadius * 0.3)
  }

  function spin() {
    if (spinning || !movies || movies.length < 1) return
    preloadSounds()
    setSpinning(true)
    setWinner(null)

    const count = movies.length
    const arcDeg = 360 / count
    const totalRotation = 360 * (5 + Math.random() * 3) + Math.random() * 360
    const duration = 4000 + Math.random() * 2000

    const startTime = Date.now()
    const startRotation = rotation
    let lastSegment = -1

    function animate() {
      const elapsed = Date.now() - startTime
      const progress = Math.min(elapsed / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      const currentRotation = startRotation + totalRotation * eased

      const currentSegment = Math.floor((currentRotation % 360) / arcDeg)
      if (currentSegment !== lastSegment) {
        lastSegment = currentSegment
        playTick(0.8 + progress * 0.6)
      }

      drawWheel(currentRotation)

      if (progress < 1) {
        animRef.current = requestAnimationFrame(animate)
      } else {
        const finalAngle = ((currentRotation % 360) + 360) % 360
        const pointerPosition = ((360 - finalAngle) % 360 + 360) % 360
        const winnerIdx = Math.floor(pointerPosition / arcDeg) % count

        setRotation(finalAngle)
        setSpinning(false)
        setWinner(movies[winnerIdx])
        playWinnerDing()
      }
    }

    animRef.current = requestAnimationFrame(animate)
  }

  const isLandscape = typeof window !== 'undefined' && window.innerWidth > window.innerHeight

  return (
    <div
      ref={containerRef}
      className="w-full flex flex-col items-center"
      style={{ gap: isLandscape ? '16px' : '20px' }}
    >
      <div className="text-center">
        <div className="text-[13px] text-[var(--fp-red)] uppercase tracking-[2px] font-semibold">
          The Movie Wheel
        </div>
        {subtitle && (
          <div className="text-xs text-[var(--fp-text-muted)] mt-1">{subtitle}</div>
        )}
      </div>

      <div className="w-0 h-0 mb-[-4px] z-10"
           style={{ borderLeft: '14px solid transparent', borderRight: '14px solid transparent', borderTop: '22px solid #fff', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))' }} />

      <canvas
        ref={canvasRef}
        width={canvasSize}
        height={canvasSize}
        className="rounded-full"
        style={{
          border: '3px solid var(--fp-gold)',
          boxShadow: '0 0 60px var(--fp-red-glow), 0 0 120px rgba(212, 175, 55, 0.15)',
          width: canvasSize,
          height: canvasSize,
        }}
      />

      <button
        onClick={spin}
        disabled={spinning || !movies || movies.length < 1}
        className="px-12 py-4 rounded-full border-none text-white text-lg font-bold tracking-wide cursor-pointer disabled:opacity-40"
        style={{
          background: 'var(--fp-red)',
          boxShadow: '0 8px 32px var(--fp-red-glow)',
        }}
      >
        {spinning ? 'Spinning…' : 'SPIN!'}
      </button>
    </div>
  )
}
